import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

import './Markdown.module.scss'; // FIXME: import as styles?

const ABSOLUTE_URL_REGEX = /^(?:https?:)?\/\//i;

// Функция для обработки текста: заменяет обратные слэши в file:// URL на двойные обратные слэши
// Это предотвращает их удаление при обработке markdown
const processFileUrls = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Находим все file:// URL с обратными слэшами и заменяем обратные слэши на двойные
  // чтобы они не удалялись при обработке markdown
  return text.replace(/(file:)(\\+)([^\s]+)/gi, (match, protocol, backslashes, path) => {
    // Заменяем каждый обратный слэш на двойной обратный слэш
    return `${protocol}${backslashes.replace(/\\/g, '\\\\')}${path}`;
  });
};

const processPreserveSlashes = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Markdown collapses `\\` to `\` (escape). To preserve exactly what user typed, we
  // automatically escape pairs of backslashes.
  const withBackslashesPreserved = text.replace(/\\\\/g, '\\\\\\\\');

  // `//something` at the start of text (or after whitespace) can be treated as a URL
  // and normalized/sanitized. Convert leading `//` to entities so it renders literally.
  return withBackslashesPreserved.replace(/(^|\s)\/\//g, (match, prefix) => `${prefix}&#47;&#47;`);
};

const Markdown = React.memo(({ linkStopPropagation, preserveSlashes, children, ...props }) => {
  const handleLinkClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const linkRenderer = useCallback(
    ({ node, ...linkProps }) => {
      // Если это file:// URL, отображаем как обычный текст (не ссылку)
      if (linkProps.href && /^file:/i.test(linkProps.href)) {
        const text = typeof linkProps.children === 'string' 
          ? linkProps.children 
          : React.Children.toArray(linkProps.children).join('');
        return <span>{text}</span>;
      }

      return (
        <a
          {...linkProps} // eslint-disable-line react/jsx-props-no-spreading
          rel={
            ABSOLUTE_URL_REGEX.test(linkProps.href) && linkProps.target === '_blank'
              ? 'noreferrer'
              : undefined
          }
          onClick={linkStopPropagation ? handleLinkClick : undefined}
        />
      );
    },
    [linkStopPropagation, handleLinkClick],
  );

  // Обрабатываем текст перед передачей в Markdown
  const processedChildren = useMemo(() => {
    if (typeof children === 'string') {
      const preserved = preserveSlashes ? processPreserveSlashes(children) : children;
      return processFileUrls(preserved);
    }
    return children;
  }, [children, preserveSlashes]);

  return (
    <ReactMarkdown
      {...props} // eslint-disable-line react/jsx-props-no-spreading
      components={{
        a: linkRenderer,
      }}
      remarkPlugins={[remarkGfm, remarkBreaks]}
      className="markdown-body"
    >
      {processedChildren}
    </ReactMarkdown>
  );
});

Markdown.propTypes = {
  linkStopPropagation: PropTypes.bool,
  preserveSlashes: PropTypes.bool,
};

Markdown.defaultProps = {
  linkStopPropagation: false,
  preserveSlashes: false,
};

export default Markdown;
